import Vue from 'vue'
import Vuex from 'vuex'
import router from '@/router'
import { getInstance } from '@/auth'

import {
  AppColor,
  UserRole,
  FunctionalUserRole,
  LanguageId,
  Locale
} from '@/config.js'

Vue.use(Vuex)

function getResidentItem (r) {
  if (r === null) return null

  return {
    id: r.id,
    name: r.name,
    container: r.container,
    kioskId: r.kiosk.id
  }
}

function getKioskItem (k) {
  if (k === null) return null
  
  return {
    id: k.id,
    serialNumber: k.serialNumber,
    name: k.name,
    description: k.description,
    container: k.container,
    facilityId: k.facility !== null ? k.facility.id : null,
    typeId: k.typeId,
    pictureUri: k.pictureUri,
    subscriptionLevel: k.subscriptionLevel,
    languageId: k.languageId
  }
}

function getResidentModel (r) {
  const isDefault = r.id === store.state.profile.defaultResidentId
  const kioskModel = getKioskModel(r.kiosk)
  return {
    id: r.id,
    firstName: r.firstName,
    lastName: r.lastName,
    name: r.name,
    container: r.container,
    pictureBase64: r.pictureBase64,
    bluetoothBeaconId: r.bluetoothBeaconId,
    kioskKeyTagId: r.kioskKeyTagId,
    languageId: r.languageId,

    // abby
    gameDifficultyLevel: r.gameDifficultyLevel,

    isDefault: isDefault,
    isActive: false,
    kiosk: kioskModel !== null
      ? kioskModel
      : { id: 0, name: 'Unknown Kiosk', codeName: 'UNKNOWN' }
  }
}

function getKioskModel (k) {
  if (k === null) return null

  return {
    id: k.id,
    serialNumber: k.serialNumber,
    name: k.name,
    description: k.description,
    typeId: k.typeId,
    type: k.typeName,
    typeCode: k.typeCode,
    container: k.container,
    isActive: false,
    hasResidents: k.hasResidents,
    pictureUri: k.pictureUri,
    subscriptionLevel: k.subscriptionLevel,
    contactUserId: k.contactUserId === null ? '' : k.contactUserId,
    contactPhone: k.contactPhone,
    contactPosition: k.contactPosition,
    contactAvailability: k.contactAvailability,
    languageId: k.languageId,
    domain: k.domain !== null
      ? {
          id: k.domain.id,
          name: k.domain.name,
          organizationName: k.domain.organizationName
        }
      : null,
    facility: k.facility !== null
      ? {
          id: k.facility.id,
          name: k.facility.name,
          geographicalDescription: k.facility.geographicalDescription
        }
      : {
          id: 0,
          name: 'Unknown Facility',
          code: 'UNKNOWN',
          geographicalDescription: 'Unknown Location'
        }
  }
}

function getActiveResident (residents) {
  if (residents === null) return null

  // see if any stored residents are currently active
  const activeResidents = residents.filter(r => {
    return r.isActive
  })

  // derive active resident
  const activeResident = activeResidents.length > 0 ? activeResidents[0] : null

  return activeResident
}

function getActiveKiosk (kiosks) {
  if (kiosks === null) return null

  // see if any stored kiosks are currently active
  const activeKiosks = kiosks.filter(k => {
    return k.isActive
  })

  // derive active kiosk
  const activeKiosk = activeKiosks.length > 0 ? activeKiosks[0] : null

  return activeKiosk
}

const state = {
  authenticated: !!localStorage.getItem('access_token'),
  accessToken: localStorage.getItem('access_token'),
  profile: {},
  kiosks: null,
  activeKiosk: null,
  kioskCount: null,
  residents: null,
  kioskResidents: null,
  activeResident: null,
  residentCount: null,
  isAdmin: null,
  isDomainManager: null,
  isKioskManager: null,
  isResidentManager: null,
  languages: null,
  contacts: null,
  kioskKeyNums: null,
  currentRoute: null,
  previousRoute: null,
  routeStack: null
}

const store = new Vuex.Store({
  state,
  getters: {
    accessToken(state) {
      return state.accessToken
    },
    authenticated(state) {
      return state.authenticated
    },
    profile: state => {
      return state.profile
    },
    residents: state => {
      return state.residents
    },
    kiosks: state => {
      return state.kiosks
    },
    activeResident: state => {
      const activeResident = getActiveResident(state.residents)
      return activeResident !== null ? activeResident : state.activeResident
    },
    activeKiosk: state => {
      const activeKiosk = getActiveKiosk(state.kiosks)
      return activeKiosk !== null ? activeKiosk : state.activeKiosk
    },
    kioskCount: state => {
      return state.kioskCount
    },
    activeResidentItem: state => {
      const activeResident = getActiveResident(state.residents)
      return activeResident !== null
        ? getResidentItem(activeResident)
        : getResidentItem(state.activeResident)
    },
    activeResidentKioskItem: state => {
      const activeResident = getActiveResident(state.residents)
      return activeResident !== null
        ? getKioskItem(activeResident.kiosk)
        : state.activeResident !== null
          ? getKioskItem(state.activeResident.kiosk)
          : null
    },
    activeKioskItem: state => {
      const activeKiosk = getActiveKiosk(state.kiosks)
      return activeKiosk !== null
        ? getKioskItem(activeKiosk)
        : getKioskItem(state.activeKiosk)
    },
    residentCount: state => {
      return state.residentCount
    },
    isAdmin: state => {
      return state.isAdmin !== null
        ? state.isAdmin
        : false
    },
    isDomainManager: state => {
      return state.isDomainManager !== null
        ? state.isDomainManager
        : false
    },
    isKioskManager: state => {
      return state.isKioskManager !== null
        ? state.isKioskManager
        : false
    },
    isResidentManager: state => {
      return state.isResidentManager !== null
        ? state.isResidentManager
        : false
    },
    languages: state => {
      return state.languages
    },
    kioskResidents: state => {
      return state.kioskResidents
    },
    contacts: state => {
      return state.contacts
    },
    kioskKeyNums: state => {
      return state.kioskKeyNums
    },
    locale: state => {
      var locale = Locale.EnglishCA
      if (state.languages !== null) {
        if (state.languages.length > 0) {
          const languageId = state.profile.languageId
          const locales = state.languages.filter(l => l.id === languageId)
          if (locales.length > 0)
            locale = locales[0].locale
        }
      }
      return locale
    },
    currentRoute: state => {
      return state.currentRoute
    },
    previousRoute: state => {
      return state.previousRoute
    },
    routeStack: state => {
      return state.routeStack
    }
  },
  mutations: {
    authenticated(state, { accessToken, payload: payload }) {
      state.accessToken = accessToken
      state.authenticated = true

      localStorage.setItem('access_token', state.accessToken)

      store.dispatch('loadProfile', { payload: payload })
    },
    updateAccessToken(state, accessToken) {
      state.accessToken = accessToken
      localStorage.setItem('access_token', state.accessToken)
    },
    updateProfile(state, profile) {
      if (profile.name !== undefined) state.profile.name = profile.name
      if (profile.nickname !== undefined) state.profile.nickname = profile.nickname
      if (profile.languageId !== undefined) state.profile.languageId = profile.languageId
      if (profile.picture !== undefined) state.profile.picture = profile.picture
      if (profile.defaultResidentId !== undefined) state.profile.defaultResidentId = profile.defaultResidentId

      // update isDefault flag
      if (state.residents !== null) {
        state.residents.forEach(r => {
          r.isDefault = r.id === profile.defaultResidentId
        })
        localStorage.setItem('residents', JSON.stringify(state.residents))
      }
    },
    updateKiosk(state, kiosk) {
      if (state.kiosks !== null) {
        const kiosks = state.kiosks.filter(k => {
          return k.id === kiosk.id
        })
        const k = kiosks.length > 0 ? kiosks[0] : null
        if (k !== null) {
          if (kiosk.hasResidents !== undefined) k.hasResidents = kiosk.hasResidents
          if (kiosk.serialNumber !== undefined) k.serialNumber = kiosk.serialNumber
          if (kiosk.name !== undefined) k.name = kiosk.name
          if (kiosk.description !== undefined) k.description = kiosk.description
          if (kiosk.languageId !== undefined) k.languageId = kiosk.languageId
          if (kiosk.contactUserId !== undefined) k.contactUserId = kiosk.contactUserId
          if (kiosk.contactPhone !== undefined) k.contactPhone = kiosk.contactPhone
          if (kiosk.contactPosition !== undefined) k.contactPosition = kiosk.contactPosition
          if (kiosk.contactAvailability !== undefined) k.contactAvailability = kiosk.contactAvailability

          const idx = state.kiosks.indexOf(k)
          state.kiosks.splice(idx, 1, k)
          localStorage.setItem('kiosks', JSON.stringify(state.kiosks))
        }
      }

      if (state.activeKiosk !== null) {   
        if (kiosk.hasResidents !== undefined) state.activeKiosk.hasResidents = kiosk.hasResidents
        if (kiosk.serialNumber !== undefined) state.activeKiosk.serialNumber = kiosk.serialNumber
        if (kiosk.name !== undefined) state.activeKiosk.name = kiosk.name
        if (kiosk.description !== undefined) state.activeKiosk.description = kiosk.description
        if (kiosk.contactUserId !== undefined) state.activeKiosk.contactUserId = kiosk.contactUserId
        if (kiosk.contactPhone !== undefined) state.activeKiosk.contactPhone = kiosk.contactPhone
        if (kiosk.contactPosition !== undefined) state.activeKiosk.contactPosition = kiosk.contactPosition
        if (kiosk.contactAvailability !== undefined) state.activeKiosk.contactAvailability = kiosk.contactAvailability
        localStorage.setItem('activeKiosk', JSON.stringify(state.activeKiosk))
      }
    },
    storeKiosks(state, kiosks) {
      state.kiosks = kiosks
      localStorage.setItem('kiosks', JSON.stringify(kiosks))
    },
    storeLoginData(state, loginData) {
      state.kiosks = loginData.kiosks
      state.kioskCount = loginData.kioskCount
      state.residents = loginData.residents
      state.isAbby = loginData.isAbby
      state.isGenie = loginData.isGenie
      state.isAdmin = loginData.isAdmin
      state.isDomainManager = loginData.isDomainManager
      state.isKioskManager = loginData.isKioskManager
      state.isResidentManager = loginData.isResidentManager
      state.isResidentManager = loginData.isResidentManager
      state.languages = loginData.languages
      localStorage.setItem('isAbby', loginData.isAbby)
      localStorage.setItem('isGenie', loginData.isGenie)
      localStorage.setItem('isAdmin', loginData.isAdmin)
      localStorage.setItem('isDomainManager', loginData.isDomainManager)
      localStorage.setItem('isKioskManager', loginData.isKioskManager)
      localStorage.setItem('isResidentManager', loginData.isResidentManager)

      if (state.kiosks !== null) {
        localStorage.setItem('kiosks', JSON.stringify(loginData.kiosks))
      }
      localStorage.setItem('kioskCount', loginData.kioskCount)

      if (state.residents !== null) {
        localStorage.setItem('residents', JSON.stringify(loginData.residents))
      }
      localStorage.setItem('residentCount', loginData.residentCount)

      if (state.languages !== null) {
        localStorage.setItem('languages', JSON.stringify(loginData.languages))
      }
    },
    storeKioskResidents(state, kioskResidents) {
      state.kioskResidents = kioskResidents

      if (state.kioskResidents !== null) {
        localStorage.setItem('kioskResidents', JSON.stringify(kioskResidents))
      }
    },
    removeKioskResident(state, id) {
      if (state.kioskResidents === null) return
      const residents = state.kioskResidents.filter(r => r.id !== id)
      state.kioskResidents = residents
      localStorage.setItem('kioskResidents', JSON.stringify(residents))
    },
    storeContacts(state, contacts) {
      state.contacts = contacts

      if (state.contacts !== null) {
        localStorage.setItem('contacts', JSON.stringify(contacts))
      }
    },
    storeKioskKeyNums(state, kioskKeyNums) {
      state.kioskKeyNums = kioskKeyNums

      if (state.kioskKeyNums !== null) {
        localStorage.setItem('kioskKeyNums', JSON.stringify(kioskKeyNums))
      }
    },
    updateKioskKeyNum(state, kioskKeyNum) {
      if (state.updateKioskKeyNum !== null) {

        // see if anything has changed
        var kioskKeyNums = state.kioskKeyNums.filter(kn => {
          return kn.id === kioskKeyNum.id && kn.residentId === kioskKeyNum.residentId
        })

        var kn = kioskKeyNums.length > 0 ? kioskKeyNums[0] : null
        if (kn !== null) return // nothing to do


        // find the existing keyTag for the resident and assign it to null
        kioskKeyNums = state.kioskKeyNums.filter(kn => {
          return kn.residentId === kioskKeyNum.residentId
        })
        
        kn = kioskKeyNums.length > 0 ? kioskKeyNums[0] : null
        if (kn !== null) {
          if (kioskKeyNum.residentId !== undefined) kn.residentId = null

          const idx = state.kioskKeyNums.indexOf(kn)
          state.kioskKeyNums.splice(idx, 1, kn)
          localStorage.setItem('kioskKeyNums', JSON.stringify(state.kioskKeyNums))
        }

        // set the residentId to the new keyTag
        kioskKeyNums = state.kioskKeyNums.filter(kn => {
          return kn.id === kioskKeyNum.id
        })

        kn = kioskKeyNums.length > 0 ? kioskKeyNums[0] : null

        if (kn !== null) {
          if (kioskKeyNum.residentId !== undefined) kn.residentId = kioskKeyNum.residentId

          const idx = state.kioskKeyNums.indexOf(kn)
          state.kioskKeyNums.splice(idx, 1, kn)
          localStorage.setItem('kioskKeyNums', JSON.stringify(state.kioskKeyNums))
        }
      }
    },
    setActiveKiosk(state, activeKiosk) {
      //-- if the signed-in user is KM or DM
      if (state.kiosks !== null) {
        // find the kiosk in their assigned list
        const kiosks = state.kiosks.filter(k => {
          return k.id === activeKiosk.id
        })

        // will always be exactly 1
        if (kiosks.length > 0) {
          // update isActive flag
          state.kiosks.forEach(k => {
            k.isActive = k.id === kiosks[0].id
          })

          state.activeKiosk = activeKiosk
          localStorage.setItem('kiosks', JSON.stringify(state.kiosks))
        }

        //-- the signed-in user is not KM or DM - store the kiosk passed in
      } else {
        activeKiosk.isActive = true
        state.activeKiosk = activeKiosk
        localStorage.setItem('activeKiosk', JSON.stringify(activeKiosk))
      }
    },
    clearActiveKiosk(state) {
      if (state.kiosks === null) return
      state.kiosks.forEach(k => {
        k.isActive = false
      })
      localStorage.setItem('kiosks', JSON.stringify(state.kiosks))
    },
    clearKioskResidents(state) {
      state.kioskResidents = null
      localStorage.setItem('kioskResidents', null)
    },
    updateKioskResident(state, resident) {
      if (state.kioskResidents !== null) {
        const residents = state.kioskResidents.filter(r => {
          return r.id === resident.id
        })
        const kr = residents.length > 0 ? residents[0] : null

        if (kr !== null) {
          if (resident.firstName !== undefined) kr.firstName = resident.firstName
          if (resident.lastName !== undefined) kr.lastName = resident.lastName
          if (resident.name !== undefined) kr.name = resident.name
          if (resident.bluetoothBeaconId !== undefined) kr.bluetoothBeaconId = resident.bluetoothBeaconId
          if (resident.kioskKeyTagId !== undefined) kr.kioskKeyTagId = resident.kioskKeyTagId
          if (resident.languageId !== undefined) kr.languageId = resident.languageId
          if (resident.picture !== undefined) kr.picture = resident.picture

          // abby
          if (resident.gameDifficultyLevel !== undefined) kr.gameDifficultyLevel = resident.gameDifficultyLevel

          const idx = state.kioskResidents.indexOf(kr)
          state.kioskResidents.splice(idx, 1, kr)
          localStorage.setItem('kioskResidents', JSON.stringify(state.kioskResidents))
        }
      }
    },
    clearKioskContacts(state) {
      state.contacts = null
      localStorage.setItem('contacts', null)
    },
    clearKioskKeyNums(state) {
      state.kioskKeyNums = null
      localStorage.setItem('kioskKeyNums', null)
    },
    setActiveResident(state, activeResident) {
      //-- if the signed-in user is RM
      if (state.residents !== null) {
        // find the resident in their assigned list
        const residents = state.residents.filter(r => {
          return r.id === activeResident.id
        })

        // will always be exatly 1
        if (residents.length > 0) {
          // update isActive flag
          state.residents.forEach(r => {
            r.isActive = r.id === residents[0].id
          })
  
          state.activeResident = activeResident
          localStorage.setItem('residents', JSON.stringify(state.residents))
        }

        //-- signed-in user is not and RM - store the resident passed in
      } else {
        activeResident.isActive = true
        state.activeResident = activeResident
        localStorage.setItem('activeResident', JSON.stringify(activeResident))
      }
    },
    updateResident(state, resident) {
      const name = (resident.firstName !== undefined && resident.lastName !== undefined)
        ? `${resident.firstName} ${resident.lastName}`
        : resident.firstName !== undefined
          ? resident.firstName
          : null

      if (state.residents !== null) {
        const residents = state.residents.filter(r => {
          return r.id === resident.id
        })
        const r = residents.length > 0 ? residents[0] : null

        if (r !== null) {
          if (resident.firstName !== undefined) r.firstName = resident.firstName
          if (resident.lastName !== undefined) r.lastName = resident.lastName
          if (resident.firstName !== undefined) r.name = name
          if (resident.bluetoothBeaconId !== undefined) r.bluetoothBeaconId = resident.bluetoothBeaconId
          if (resident.kioskKeyTagId !== undefined) r.kioskKeyTagId = resident.kioskKeyTagId
          if (resident.languageId !== undefined) r.languageId = resident.languageId
          if (resident.pictureBase64 !== undefined) r.pictureBase64 = resident.pictureBase64

          // abby
          if (resident.gameDifficultyLevel !== undefined) r.gameDifficultyLevel = resident.gameDifficultyLevel

          const idx = state.residents.indexOf(r)
          state.residents.splice(idx, 1, r)
          localStorage.setItem('residents', JSON.stringify(state.residents))
        }
      }

      if (state.activeResident !== null) {          
        if (resident.firstName !== undefined) state.activeResident.firstName = resident.firstName
        if (resident.lastName !== undefined) state.activeResident.lastName = resident.lastName
        if (resident.firstName !== undefined) state.activeResident.name = name
        if (resident.bluetoothBeaconId !== undefined) state.activeResident.bluetoothBeaconId = resident.bluetoothBeaconId
		if (resident.kioskKeyTagId !== undefined) state.activeResident.kioskKeyTagId = resident.kioskKeyTagId
        if (resident.languageId !== undefined) state.activeResident.languageId = resident.languageId
        if (resident.pictureBase64 !== undefined) state.activeResident.pictureBase64 = resident.pictureBase64

        // abby
        state.activeResident.gameDifficultyLevel = resident.gameDifficultyLevel

        localStorage.setItem('activeResident', JSON.stringify(state.activeResident))
      }
    },
    clearActiveResident(state) {
      if (state.residents === null) return
      state.residents.forEach(r => {
        r.isActive = false
      })
      localStorage.setItem('residents', JSON.stringify(state.residents))
    },
    // when home page loads
    initRouteStack(state) {
      state.routeStack = ['Home']
      localStorage.setItem('routeStack', JSON.stringify(state.routeStack))
      
      state.currentRoute = 'Home'
      localStorage.setItem('previousRoute', state.previousRoute)

      state.previousRoute = 'Home'
      localStorage.setItem('currentRoute', state.currentRoute)
    },
    // when any redirect link is clicked except back arrow or breadcrumb
    pushRoute(state, name) {
      let routeStack = JSON.parse(localStorage.getItem('routeStack'))
      if (routeStack === null) routeStack = ['Home']

      const previousRoute = routeStack[routeStack.length - 1]
      routeStack.push(name)

      state.routeStack = routeStack
      localStorage.setItem('routeStack', JSON.stringify(state.routeStack))

      state.previousRoute = previousRoute
      localStorage.setItem('previousRoute', state.previousRoute)

      state.currentRoute = name
      localStorage.setItem('currentRoute', name)
    },
    // when a back button is clicked
    popRoute() {
      let routeStack = state.routeStack.map(rs => rs);
      if (routeStack.length === 0) routeStack = ['Home']

      if (routeStack.length > 1) routeStack.pop()

      // TODO: why does this work?
      // shouldn't currentRoute be [routeStack.length] ?
      const previousRoute = routeStack[routeStack.length - 1]
      const currentRoute = routeStack[routeStack.length - 1]
      
      state.routeStack = routeStack.map(rs => rs)
      localStorage.setItem('routeStack', JSON.stringify(state.routeStack))

      state.previousRoute = previousRoute
      localStorage.setItem('previousRoute', state.previousRoute)

      state.currentRoute = currentRoute
      localStorage.setItem('currentRoute', state.currentRoute)
    },
    // when a breadcrumb is clicked
    setRoute(state, name) {
      let routeStack = state.routeStack.map(rs => rs)

      let routeIndex = routeStack.indexOf(name)
      const routeCount = routeStack.length

      if (routeIndex >= 0) {
        for (var i = routeCount; i > routeIndex + 1; i--) { routeStack.pop() }

        if (routeStack.length === 0) routeStack = ['Home']
        let previousRoute = "Home"
        previousRoute = routeStack[routeStack.length - 1]
        
        state.routeStack = routeStack.map(rs => rs)
        localStorage.setItem('routeStack', JSON.stringify(state.routeStack))
        
        state.currentRoute = name
        localStorage.setItem('currentRoute', name)

        state.previousRoute = previousRoute
        localStorage.setItem('previousRoute', state.previousRoute)
      }
    },
    logout(state) {
      state.authenticated = false
      state.accessToken = null
      state.isAdmin = null
      state.isDomainManager = null
      state.isKioskManager = null
      state.isResidentManager = null
      state.profile = {}
      state.residents = null
      state.kioskResidents = null
      state.activeResident = null
      state.activeKiosk = null
      state.contacts = null
      state.kioskKeyNums = null
      state.currentRoute = null
      state.previousRoute = null
      state.routeStack = null
      
      localStorage.removeItem('access_token')
      localStorage.removeItem('kiosks')
      localStorage.removeItem('activeKiosk')
      localStorage.removeItem('kioskCount')
      localStorage.removeItem('residents')
      localStorage.removeItem('kioskResidents')
      localStorage.removeItem('activeResident')
      localStorage.removeItem('residentCount')
      localStorage.removeItem('isAdmin')
      localStorage.removeItem('isDomainManager')
      localStorage.removeItem('isKioskManager')
      localStorage.removeItem('isResidentManager')
      localStorage.removeItem('languages')
      localStorage.removeItem('contacts')
      localStorage.removeItem('kioskKeyNums')
      localStorage.removeItem('currentRoute')
      localStorage.removeItem('previousRoute')
      localStorage.removeItem('routeStack')

      const authInstance = getInstance()
      authInstance.logout()
    }
  },
  actions: {
    logout({ commit }) {
      commit('logout')
    },

    authenticated({ commit }, { accessToken, payload: payload }) {
      commit('authenticated', { accessToken, payload })
    },

    updateAccessToken({ commit }, accessToken) {
      commit('updateAccessToken', accessToken)
    },

    updateProfile({ commit }, { profile }) {
      commit('updateProfile', profile)
    },

    storeLoginData({ commit }, { loginData }) {
      if (loginData.residents !== null) {
        var residentModels = []
        const residents = loginData.residents
        residents.forEach(r => {
          const residentModel = getResidentModel(r)
          residentModels.push(residentModel)
        })
        loginData.residents = residentModels
        loginData.residentCount = residentModels !== null
          ? residentModels.length
          : 0
      }

      if (loginData.kiosks !== null) {
        var kioskModels = []
        const kiosks = loginData.kiosks
        kiosks.forEach(k => {
          const kioskModel = getKioskModel(k)
          kioskModels.push(kioskModel)
        })
        loginData.kiosks = kioskModels
        loginData.kioskCount = kioskModels !== null
          ? kioskModels.length
          : 0
      }

      commit('storeLoginData', loginData)
    },

    storeKioskResidents({ commit }, { kioskResidents }) {
      commit('storeKioskResidents', kioskResidents)
    },

    removeKioskResident({ commit }, { id }) {
      commit('removeKioskResident', id)
    },

    storeContacts({ commit }, { contacts }) {
      commit('storeContacts', contacts)
    },

    storeKioskKeyNums({ commit }, { kioskKeyNums }) {
      commit('storeKioskKeyNums', kioskKeyNums)
    },

    updateKioskKeyNum({ commit }, { kioskKeyNum }) {
      commit('updateKioskKeyNum', kioskKeyNum)
    },


    setActiveKiosk({ commit }, { activeKiosk }) {
      const kioskModel = getKioskModel(activeKiosk)
      commit('setActiveKiosk', kioskModel)
    },

    updateKiosk({ commit }, { kiosk }) {
      commit('updateKiosk', kiosk)
    },

    clearActiveKiosk({ commit }) {
      commit('clearActiveKiosk')
    },

    clearKioskResidents({ commit }) {
      commit('clearKioskResidents')
    },

    updateKioskResident({ commit }, { resident }) {
      commit('updateKioskResident', resident)
    },

    clearKioskContacts({ commit }) {
      commit('clearKioskContacts')
    },

    clearKioskKeyNums({ commit }) {
      commit('clearKioskKeyNums')
    },

    setActiveResident( { commit }, { activeResident }) {
      const residentModel = getResidentModel(activeResident)
      commit('setActiveResident', residentModel)
    },

    updateResident({ commit }, { resident }) {
      commit('updateResident', resident)
    },

    clearActiveResident({ commit }) {
      commit('clearActiveResident')
    },

    loadProfile(context, { payload }) {
      const userMetadata =
        payload['https://ambientactivity.com/user_metadata'] !== undefined
          ? payload['https://ambientactivity.com/user_metadata']
          : {}
      const appMetadata =
        payload['https://ambientactivity.com/app_metadata'] !== undefined
          ? payload['https://ambientactivity.com/app_metadata']
          : {}

      // make the metadata safe
      const languageId = userMetadata.languageId !== undefined
          ? userMetadata.languageId
          : payload.languageId ?? LanguageId.English

      const picture = userMetadata.picture !== undefined
          ? userMetadata.picture
          : payload.picture
          
      const domainIds = appMetadata.domainIds !== undefined && appMetadata.domainIds !== null
        ? appMetadata.domainIds
        : []

      const facilityIds = appMetadata.facilityIds !== undefined && appMetadata.facilityIds !== null
        ? appMetadata.facilityIds
        : []

      const kioskIds = appMetadata.kioskIds !== undefined && appMetadata.kioskIds !== null
        ? appMetadata.kioskIds
        : []

      const residentIds = appMetadata.residentIds !== undefined &&
        appMetadata.residentIds !== null
          ? appMetadata.residentIds
          : []
      const defaultResidentId = userMetadata.defaultResidentId !== undefined &&
        appMetadata.defaultResidentId !== null
          ? userMetadata.defaultResidentId
          : residentIds.length > 0
            ? residentIds[0]
            : null

      const role = appMetadata.role !== undefined && appMetadata.role !== null
          ? appMetadata.role
          : UserRole.Guest

      var functionalRole = FunctionalUserRole.Guest
      var functionalRoleColor = AppColor.RoleGuest

      // derive functional role
      if (role === UserRole.Admin) functionalRole = FunctionalUserRole.Admin
      else {
        if (role === UserRole.Manager) {
          // if (facilityIds.length > 0) {
          //   functionalRole = FunctionalUserRole.KioskAdministrator
          if (domainIds.length > 0) {
              functionalRole = FunctionalUserRole.DomainManager
          } else if (kioskIds.length > 0) {
            functionalRole = FunctionalUserRole.KioskManager
          } else if (residentIds.length > 0) {
            functionalRole = FunctionalUserRole.ResidentManager
          }
        }
      }
      if (functionalRole === UserRole.Admin) {
        functionalRoleColor = AppColor.RoleAdmin
      } else if (functionalRole === FunctionalUserRole.DomainManager) {
        functionalRoleColor = AppColor.RoleDomainManager
      } else if (functionalRole === FunctionalUserRole.KioskManager) {
        functionalRoleColor = AppColor.RoleKioskManager
      } else if (functionalRole === FunctionalUserRole.ResidentManager) {
        functionalRoleColor = AppColor.RoleResidentManager
      } else if (functionalRole === UserRole.Guest) {
        functionalRoleColor = AppColor.RoleGuest
      }

      const profile = {
        id: payload.sub,
        email: payload.email,
        name: payload.name,
        nickname: payload.nickname,
        languageId: languageId,
        picture: picture,
        domainIds: domainIds,
        facilityIds: facilityIds,
        kioskIds: kioskIds,
        residentIds: residentIds,
        defaultResidentId: defaultResidentId,
        role: role,
        functionalRole: functionalRole,
        functionalRoleColor: functionalRoleColor
      }
      store.state.profile = profile

      // -------- custom restore from local storage -----------

      // facilities
      if (localStorage.getItem('facilities') !== undefined) {
        const facilities = JSON.parse(localStorage.getItem('facilities'))
        store.state.facilities = facilities
      }

      // kiosks
      if (localStorage.getItem('kiosks') !== undefined) {
        const kiosks = JSON.parse(localStorage.getItem('kiosks'))
        store.state.kiosks = kiosks
        store.state.kioskCount = kiosks !== null
          ? kiosks.length
          : 0
      }
      
      // residents
      if (localStorage.getItem('residents') !== undefined) {
        const residents = JSON.parse(localStorage.getItem('residents'))
        store.state.residents = residents
        store.state.residentCount = residents !== null
          ? residents.length
          : 0
      }

      // activeResident
      if (localStorage.getItem('activeResident') !== undefined) {
        const activeResident = JSON.parse(
          localStorage.getItem('activeResident')
        )
        store.state.activeResident = activeResident
      }

      // activeKiosk
      if (localStorage.getItem('activeKiosk') !== undefined) {
        const activeKiosk = JSON.parse(localStorage.getItem('activeKiosk'))
        store.state.activeKiosk = activeKiosk
      }

      // roles
      if (localStorage.getItem('isAdmin') !== undefined) {
        const isAdmin = localStorage.getItem('isAdmin')
        store.state.isAdmin = isAdmin === 'true'
      }
      
      if (localStorage.getItem('isDomainManager') !== undefined) {
        const isDomainManager = localStorage.getItem('isDomainManager')
        store.state.isDomainManager = isDomainManager === 'true'
      }

      if (localStorage.getItem('isKioskManager') !== undefined) {
        const isKioskManager = localStorage.getItem('isKioskManager')
        store.state.isKioskManager = isKioskManager === 'true'
      }

      if (localStorage.getItem('isResidentManager') !== undefined) {
        const isResidentManager = localStorage.getItem('isResidentManager')
        store.state.isResidentManager = isResidentManager === 'true'
      }

      // languages
      if (localStorage.getItem('languages') !== undefined) {
        const languages = JSON.parse(localStorage.getItem('languages'))
        store.state.languages = languages
      }

      // kiosk contacts
      if (localStorage.getItem('contacts') !== undefined) {
        const contacts = JSON.parse(localStorage.getItem('contacts'))
        store.state.contacts = contacts
      }

      // kiosk residents
      if (localStorage.getItem('kioskResidents') !== undefined) {
        const kioskResidents = JSON.parse(localStorage.getItem('kioskResidents'))
        store.state.kioskResidents = kioskResidents
      }

      // currentRoute
      if (localStorage.getItem('currentRoute') !== undefined) {
        store.state.currentRoute = localStorage.getItem('currentRoute')
      }

      // previousRoute
      if (localStorage.getItem('previousRoute') !== undefined) {
        store.state.previousRoute = localStorage.getItem('previousRoute')
      }

      // routeStack
      if (localStorage.getItem('routeStack') !== undefined) {
        store.state.routeStack = JSON.parse(localStorage.getItem('routeStack'))
      }
    },

    initRouteStack({ commit }) {
      commit('initRouteStack')
    },

    pushRoute({ commit }, { name }) {
      commit('pushRoute', name)
    },

    popRoute({ commit }) {
      commit('popRoute')
    },

    setRoute({ commit }, { name }) {
      commit('setRoute', name)
    },

    // used after they refresh the browser
    setDefaultRoute() {
      const route = localStorage.getItem('currentRoute')
      router.push(route !== null ? route : '/')
    }
  }
})

export default store
